// @flow
import React, { useContext } from 'react';
import classNames from 'classnames';
import { constants } from '@clubmed/utils';

import ReactResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { addTranslationKeys, t } from 'Src/i18n';

const { MEETING_ROOM_SHAPES } = constants;

const SHAPE_NOT_AVAILABLE = '-';
const getMeetingRoomCapacityByShape = ({
  meetingRoom,
  shape,
}: {
  meetingRoom: MeetingRoom,
  shape: string,
}): string => {
  const meetingRoomShape = meetingRoom.shapes.find(({ type }) => type === shape);
  return meetingRoomShape && meetingRoomShape.maxCapacity
    ? t('productMeetingRoomInfoLine.people', { max_capacity: meetingRoomShape.maxCapacity })
    : SHAPE_NOT_AVAILABLE;
};

declare type MeetingRoom = {
  label: string,
  length: ?Measure,
  width: ?Measure,
  area: ?Measure,
  shapes: Array<{
    type: string,
    maxCapacity: number,
  }>,
};

type Props = {
  meetingRoomsTitle: string,
  ushape: string,
  boardroom: string,
  classroom: string,
  theatre: string,
  people: string,
  lengthWidth: string,
  area: string,
  lengthWidthAreaSeparator: string,
  productMeetingRooms: {
    availableMeetingRoomShapes: Array<string>,
    meetingRooms: Array<MeetingRoom>,
  },
};
export const ProductMeetingRoomInfoLine = ({ productMeetingRooms, ...translationKeys }: Props) => {
  const { isTablet, isRTL } = useContext(ReactResponsiveContext);
  addTranslationKeys({
    productMeetingRoomInfoLine: translationKeys,
  });
  if (
    productMeetingRooms.meetingRooms.length === 0 ||
    productMeetingRooms.availableMeetingRoomShapes.length === 0
  ) {
    return null;
  }
  const { availableMeetingRoomShapes, meetingRooms } = productMeetingRooms;

  const shapeLabels = {
    [MEETING_ROOM_SHAPES.USHAPE]: t('productMeetingRoomInfoLine.ushape'),
    [MEETING_ROOM_SHAPES.BOARDROOM]: t('productMeetingRoomInfoLine.boardroom'),
    [MEETING_ROOM_SHAPES.CLASSROOM]: t('productMeetingRoomInfoLine.classroom'),
    [MEETING_ROOM_SHAPES.THEATRE]: t('productMeetingRoomInfoLine.theatre'),
  };
  const headerCellStyle =
    'bg-blue text-white h-16 font-brand text-md text-center font-normal leading-tight';
  const firstColStickyStyle =
    'border-none px-6 absolute md:static rtl:left-0 md:inset-auto break-words';
  const contentRowClasses = 'text-center h-16 text-base font-sans leading-one';
  const stickyWrapperStyle = isRTL
    ? { marginRight: !isTablet ? '12.1rem' : undefined }
    : { marginLeft: !isTablet ? '12.1rem' : undefined };
  const colBorderStyle = !isTablet ? '2px solid white' : undefined;
  const colBorder = isRTL ? { borderLeft: colBorderStyle } : { borderRight: colBorderStyle };
  const firstColWidth = !isTablet ? '12rem' : undefined;

  const contentToRender = (
    <div
      className={classNames('mt-3 mb-5 md:my-16 md:w-full overflow-x-scroll md:overflow-x-hidden', {
        Line: !isTablet,
      })}
      style={stickyWrapperStyle}
    >
      <table className="w-full" style={{ borderSpacing: isTablet ? '2px' : '0px' }}>
        <thead>
          <tr>
            <th
              className={`${firstColStickyStyle} ${headerCellStyle} flex justify-center items-center`}
              style={{ width: firstColWidth }}
            >
              {t('productMeetingRoomInfoLine.meetingRoomsTitle')}
            </th>
            {availableMeetingRoomShapes.map(shape => (
              <th
                key={shape}
                className={headerCellStyle}
                style={{
                  minWidth: '8rem',
                  ...colBorder,
                }}
              >
                {shapeLabels[shape]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {meetingRooms.map((meetingRoom, index) => (
            <tr key={meetingRoom.label} className={`${contentRowClasses}`}>
              <td
                className={classNames(firstColStickyStyle, {
                  'bg-gray-lighter': index % 2 === 0,
                })}
                style={{
                  width: firstColWidth,
                  lineHeight:
                    !meetingRoom.length && !meetingRoom.width && !meetingRoom.area ? 3 : undefined,
                }}
              >
                <p className="font-bold break-words truncate">{meetingRoom.label}</p>
                <p>
                  {meetingRoom.length && meetingRoom.width
                    ? t('productMeetingRoomInfoLine.lengthWidth', {
                        length_value: meetingRoom.length?.value || '',
                        width_value: meetingRoom.width?.value || '',
                        length_width_unit:
                          meetingRoom.length?.unit || meetingRoom.width?.unit || '',
                      })
                    : null}
                  {meetingRoom.length && meetingRoom.width && meetingRoom.area
                    ? ` ${t('productMeetingRoomInfoLine.lengthWidthAreaSeparator')} `
                    : null}
                  {meetingRoom.area
                    ? t('productMeetingRoomInfoLine.area', {
                        area_value: meetingRoom.area?.value || '',
                        area_unit: meetingRoom.area?.unit || '',
                      })
                    : null}
                </p>
              </td>
              {availableMeetingRoomShapes.map(shape => {
                const meetingRoomCapacity = getMeetingRoomCapacityByShape({
                  meetingRoom,
                  shape,
                });
                return (
                  <td
                    key={shape}
                    className={classNames('leading-tight', {
                      'bg-gray-lighter': index % 2 === 0,
                    })}
                    style={{
                      minWidth: '8rem',
                      lineHeight: '5',
                      ...colBorder,
                    }}
                    aria-hidden={meetingRoomCapacity === SHAPE_NOT_AVAILABLE}
                  >
                    {meetingRoomCapacity}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return isTablet ? (
    <SiteContainer className="Line">{contentToRender}</SiteContainer>
  ) : (
    contentToRender
  );
};
